/**
 * Which countries and campaigns are actually configured across a tab's rules.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Drives the "• N" markers in the dropdowns, so an operator can see which
 * entries carry a value without opening every rule.
 */

import type { TagTrafficProvider } from '@trafficpulse/shared';

import { normScope } from './scope';

/**
 * Structural, so one function serves both rule systems. `kind` is declared only
 * to satisfy TypeScript's weak-type rule: a FLOW ranking condition carries
 * neither threshold array, and an all-optional target rejects a source with no
 * properties in common. Ranking conditions legitimately have no overrides.
 */
interface ConditionLike {
  kind?: unknown;
  countryThresholds?: ReadonlyArray<{ countryKey: string }>;
  campaignThresholds?: ReadonlyArray<{ provider: TagTrafficProvider; campaignId: string }>;
}

interface RuleLike {
  conditions?: ReadonlyArray<ConditionLike>;
}

export interface OverrideUsage {
  /** countryKey (lowercased) → how many conditions override it. */
  countries: Map<string, number>;
  /** `PROVIDER:id` → how many conditions override it. */
  campaigns: Map<string, number>;
}

const bump = (m: Map<string, number>, key: string): void => {
  m.set(key, (m.get(key) ?? 0) + 1);
};

export function overridesInUse(rules: ReadonlyArray<RuleLike> | undefined): OverrideUsage {
  const countries = new Map<string, number>();
  const campaigns = new Map<string, number>();
  for (const rule of rules ?? []) {
    for (const c of rule.conditions ?? []) {
      for (const t of c.countryThresholds ?? []) {
        const key = normScope(t.countryKey ?? '');
        if (key) bump(countries, key);
      }
      for (const t of c.campaignThresholds ?? []) {
        const id = (t.campaignId ?? '').trim();
        if (id && t.provider) bump(campaigns, `${t.provider}:${normScope(id)}`);
      }
    }
  }
  return { countries, campaigns };
}
